import { useState, useEffect, useRef, useCallback } from 'react';

export interface Draft {
  id: string;
  prompt: string;
  selectedType: string;
  duration: string;
  aspectRatio: string;
  style: string;
  resolution: string;
  audioOption: string;
  savedAt: number;
  label: string;
}

export interface DraftConfig {
  prompt: string;
  selectedType: string;
  duration: string;
  aspectRatio: string;
  style: string;
  resolution: string;
  audioOption: string;
}

interface PromptHistoryItem {
  id: string;
  text: string;
  capturedAt: number;
  charCount: number;
}

interface DraftsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentConfig: DraftConfig;
  onRestore: (config: DraftConfig) => void;
  darkMode: boolean;
}

const STORAGE_KEY = 'ai-video-drafts';
const HISTORY_STORAGE_KEY = 'ai-video-prompt-history';
const MAX_DRAFTS = 12;
const MAX_HISTORY = 50;
const DEBOUNCE_MS = 3000;

function formatTimeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'Just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function autoLabel(prompt: string): string {
  const trimmed = prompt.trim();
  if (!trimmed) return 'Untitled draft';
  return trimmed.length <= 40 ? trimmed : trimmed.slice(0, 40).trimEnd() + '…';
}

function configsEqual(a: DraftConfig, b: DraftConfig): boolean {
  return (
    a.prompt === b.prompt &&
    a.selectedType === b.selectedType &&
    a.duration === b.duration &&
    a.aspectRatio === b.aspectRatio &&
    a.style === b.style &&
    a.resolution === b.resolution &&
    a.audioOption === b.audioOption
  );
}

const STYLE_ICONS: Record<string, string> = {
  Cinematic: 'ri-film-line',
  Realistic: 'ri-camera-line',
  Animated: 'ri-gamepad-line',
  Abstract: 'ri-bubble-chart-line',
};

const TYPE_LABELS: Record<string, string> = {
  marketing: 'Marketing',
  film: 'Film/Cinema',
  documentary: 'Documentary',
  social: 'Social Media',
  explainer: 'Explainer',
  animation: 'Animation',
};

export default function DraftsPanel({ isOpen, onClose, currentConfig, onRestore, darkMode }: DraftsPanelProps) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [promptHistory, setPromptHistory] = useState<PromptHistoryItem[]>([]);
  const [activeTab, setActiveTab] = useState<'drafts' | 'history'>('drafts');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const lastSavedRef = useRef<DraftConfig | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const historyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastHistoryPromptRef = useRef<string>('');

  // Load from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setDrafts(JSON.parse(raw));
    } catch { /* ignore */ }
    try {
      const rawH = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (rawH) setPromptHistory(JSON.parse(rawH));
    } catch { /* ignore */ }
  }, []);

  // Persist drafts
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts)); } catch { /* ignore */ }
  }, [drafts]);

  // Persist prompt history
  useEffect(() => {
    try { localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(promptHistory)); } catch { /* ignore */ }
  }, [promptHistory]);

  // Debounced prompt history capture
  const capturePromptHistory = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length < 15) return;
    if (trimmed === lastHistoryPromptRef.current) return;
    lastHistoryPromptRef.current = trimmed;
    const entry: PromptHistoryItem = {
      id: `ph-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      text: trimmed,
      capturedAt: Date.now(),
      charCount: trimmed.length,
    };
    setPromptHistory(prev => {
      // Deduplicate: skip if last entry is the same
      if (prev.length > 0 && prev[0].text === trimmed) return prev;
      return [entry, ...prev].slice(0, MAX_HISTORY);
    });
  }, []);

  useEffect(() => {
    if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
    if (currentConfig.prompt.trim().length >= 15) {
      historyDebounceRef.current = setTimeout(() => {
        capturePromptHistory(currentConfig.prompt);
      }, DEBOUNCE_MS);
    }
    return () => { if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current); };
  }, [currentConfig.prompt, capturePromptHistory]);

  // Auto-save countdown
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setCountdown(30);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          autoSave();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConfig]);

  const autoSave = () => {
    if (!currentConfig.prompt.trim()) return;
    if (lastSavedRef.current && configsEqual(lastSavedRef.current, currentConfig)) return;
    saveDraft(false);
  };

  const saveDraft = (manual = true) => {
    if (!currentConfig.prompt.trim()) return;
    const newDraft: Draft = {
      id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      ...currentConfig,
      savedAt: Date.now(),
      label: autoLabel(currentConfig.prompt),
    };
    setDrafts(prev => [newDraft, ...prev].slice(0, MAX_DRAFTS));
    lastSavedRef.current = { ...currentConfig };
    if (manual) {
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    }
    setCountdown(30);
  };

  const deleteDraft = (id: string) => {
    setDrafts(prev => prev.filter(d => d.id !== id));
  };

  const clearAllDrafts = () => { setDrafts([]); };
  const clearAllHistory = () => { setPromptHistory([]); lastHistoryPromptRef.current = ''; };

  const startEdit = (draft: Draft) => {
    setEditingId(draft.id);
    setEditLabel(draft.label);
  };

  const commitEdit = () => {
    if (!editingId) return;
    setDrafts(prev => prev.map(d => d.id === editingId ? { ...d, label: editLabel.trim() || autoLabel(d.prompt) } : d));
    setEditingId(null);
  };

  const restoreHistoryPrompt = (item: PromptHistoryItem) => {
    onRestore({ ...currentConfig, prompt: item.text });
    onClose();
  };

  const base = darkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const cardBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200';
  const hoverCard = darkMode ? 'hover:border-orange-600' : 'hover:border-orange-400';

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      )}

      <div
        className={`fixed top-0 left-0 bottom-0 z-50 w-full max-w-sm flex flex-col border-r transition-transform duration-300 ${base} ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-5 flex-shrink-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <i className="ri-draft-line text-white text-lg"></i>
              <h3 className="text-white font-bold text-lg">My Drafts</h3>
              <span className="text-white/80 text-xs font-semibold bg-white/20 px-2 py-0.5 rounded-full">
                {drafts.length}/{MAX_DRAFTS}
              </span>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/20 text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          <p className="text-white/75 text-xs">Configs auto-saved every 30s · Prompts logged as you type</p>

          {/* Auto-save progress bar */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 bg-white/20 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-1000"
                style={{ width: `${((30 - countdown) / 30) * 100}%` }}
              />
            </div>
            <span className="text-white/80 text-[10px] font-mono flex-shrink-0 w-14">
              {savedFlash ? '✓ Saved!' : `Auto-save ${countdown}s`}
            </span>
          </div>
        </div>

        {/* Tab switcher */}
        <div className={`flex border-b flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('drafts')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-bold transition-all cursor-pointer border-b-2 ${
              activeTab === 'drafts'
                ? darkMode ? 'border-orange-500 text-orange-400' : 'border-orange-500 text-orange-600'
                : `border-transparent ${muted} hover:text-gray-700 dark:hover:text-gray-300`
            }`}
          >
            <i className="ri-save-line text-sm"></i>
            Saved Configs
            {drafts.length > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                activeTab === 'drafts'
                  ? darkMode ? 'bg-orange-900/40 text-orange-400' : 'bg-orange-100 text-orange-600'
                  : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
              }`}>{drafts.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-bold transition-all cursor-pointer border-b-2 ${
              activeTab === 'history'
                ? darkMode ? 'border-orange-500 text-orange-400' : 'border-orange-500 text-orange-600'
                : `border-transparent ${muted} hover:text-gray-700 dark:hover:text-gray-300`
            }`}
          >
            <i className="ri-history-line text-sm"></i>
            Prompt History
            {promptHistory.length > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                activeTab === 'history'
                  ? darkMode ? 'bg-orange-900/40 text-orange-400' : 'bg-orange-100 text-orange-600'
                  : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
              }`}>{promptHistory.length}</span>
            )}
          </button>
        </div>

        {/* ─────────────────────── DRAFTS TAB ─────────────────────── */}
        {activeTab === 'drafts' && (
          <>
            {/* Action row */}
            <div className={`px-4 py-3 border-b flex items-center gap-2 flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => saveDraft(true)}
                disabled={!currentConfig.prompt.trim()}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  savedFlash
                    ? 'bg-green-500 text-white'
                    : currentConfig.prompt.trim()
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:shadow-md'
                    : darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                }`}
              >
                <i className={`${savedFlash ? 'ri-check-line' : 'ri-save-line'} text-sm`}></i>
                {savedFlash ? 'Saved!' : 'Save Now'}
              </button>
              {drafts.length > 0 && (
                <button
                  onClick={clearAllDrafts}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                    darkMode ? 'bg-gray-800 text-red-400 hover:bg-red-900/30' : 'bg-gray-100 text-red-500 hover:bg-red-50'
                  }`}
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              )}
            </div>

            {/* Current config snapshot */}
            {currentConfig.prompt.trim() && (
              <div className={`mx-4 mt-3 mb-1 rounded-xl border-2 border-dashed p-3 flex-shrink-0 ${
                darkMode ? 'border-orange-700 bg-orange-900/10' : 'border-orange-300 bg-orange-50'
              }`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                    Current unsaved config
                  </span>
                </div>
                <p className={`text-xs font-medium line-clamp-1 mb-1.5 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {currentConfig.prompt.slice(0, 60)}{currentConfig.prompt.length > 60 ? '…' : ''}
                </p>
                <div className="flex flex-wrap gap-1">
                  {[currentConfig.style, currentConfig.duration, TYPE_LABELS[currentConfig.selectedType] ?? currentConfig.selectedType].map(tag => (
                    <span key={tag} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-white border border-gray-200 text-gray-600'
                    }`}>{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Drafts list */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {drafts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <i className={`ri-draft-line text-2xl ${muted}`}></i>
                  </div>
                  <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>No drafts yet</p>
                  <p className={`text-xs ${muted} max-w-[200px]`}>Start typing a prompt — it&apos;ll auto-save here every 30 seconds</p>
                </div>
              ) : (
                drafts.map((draft) => (
                  <div
                    key={draft.id}
                    className={`rounded-xl border p-3 transition-all duration-200 cursor-pointer ${cardBg} ${hoverCard}`}
                    onClick={() => { onRestore(draft); onClose(); }}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        darkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}>
                        <i className={`${STYLE_ICONS[draft.style] ?? 'ri-video-line'} text-sm text-orange-500`}></i>
                      </div>

                      <div className="flex-1 min-w-0">
                        {editingId === draft.id ? (
                          <input
                            autoFocus
                            value={editLabel}
                            onChange={e => setEditLabel(e.target.value.slice(0, 60))}
                            onBlur={commitEdit}
                            onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditingId(null); }}
                            onClick={e => e.stopPropagation()}
                            className={`w-full text-xs font-semibold border rounded px-1.5 py-0.5 mb-1 ${
                              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        ) : (
                          <div className="flex items-center gap-1 mb-0.5">
                            <p className={`text-xs font-semibold truncate flex-1 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                              {draft.label}
                            </p>
                            <button
                              onClick={e => { e.stopPropagation(); startEdit(draft); }}
                              className={`w-5 h-5 flex items-center justify-center rounded hover:opacity-100 transition-opacity flex-shrink-0 ${muted}`}
                            >
                              <i className="ri-pencil-line text-[10px]"></i>
                            </button>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1 mb-1">
                          {[draft.style, draft.duration, TYPE_LABELS[draft.selectedType] ?? draft.selectedType].map(tag => (
                            <span key={tag} className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                              darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                            }`}>{tag}</span>
                          ))}
                        </div>
                        <div className={`flex items-center justify-between text-[10px] ${muted}`}>
                          <span><i className="ri-time-line mr-0.5"></i>{formatTimeAgo(draft.savedAt)}</span>
                          <span className="text-orange-500 font-semibold">Click to restore</span>
                        </div>
                      </div>

                      <button
                        onClick={e => { e.stopPropagation(); deleteDraft(draft.id); }}
                        className={`w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0 transition-all hover:bg-red-500 hover:text-white cursor-pointer ${muted}`}
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* ─────────────────────── HISTORY TAB ─────────────────────── */}
        {activeTab === 'history' && (
          <>
            {/* History action row */}
            <div className={`px-4 py-3 border-b flex items-center justify-between flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full bg-green-500 animate-pulse`}></div>
                <span className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Auto-logging as you type
                </span>
              </div>
              {promptHistory.length > 0 && (
                <button
                  onClick={clearAllHistory}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                    darkMode ? 'bg-gray-800 text-red-400 hover:bg-red-900/30' : 'bg-gray-100 text-red-500 hover:bg-red-50'
                  }`}
                >
                  <i className="ri-delete-bin-line mr-1 text-xs"></i>Clear All
                </button>
              )}
            </div>

            {/* How it works hint */}
            <div className={`mx-4 mt-3 mb-1 rounded-xl p-3 flex items-start gap-2.5 flex-shrink-0 ${
              darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-blue-50 border border-blue-100'
            }`}>
              <i className={`ri-information-line text-sm mt-0.5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-blue-400'}`}></i>
              <p className={`text-[11px] leading-relaxed ${darkMode ? 'text-gray-400' : 'text-blue-600'}`}>
                Every prompt you type is automatically logged here after 3 seconds of inactivity — so you never lose something you typed earlier. Click any entry to load it.
              </p>
            </div>

            {/* History list */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {promptHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <i className={`ri-history-line text-2xl ${muted}`}></i>
                  </div>
                  <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>No history yet</p>
                  <p className={`text-xs ${muted} max-w-[200px]`}>Type a prompt (15+ characters) and it will appear here automatically</p>
                </div>
              ) : (
                promptHistory.map((item, idx) => (
                  <button
                    key={item.id}
                    onClick={() => restoreHistoryPrompt(item)}
                    className={`w-full text-left rounded-xl border p-3 transition-all duration-200 cursor-pointer ${cardBg} ${hoverCard} group`}
                  >
                    <div className="flex items-start gap-2.5">
                      {/* Index indicator */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold mt-0.5 ${
                        idx === 0
                          ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white'
                          : darkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'
                      }`}>
                        {idx === 0 ? <i className="ri-bookmark-fill text-[10px]"></i> : idx + 1}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={`text-xs leading-relaxed mb-1.5 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}
                          style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {item.text}
                        </p>
                        <div className={`flex items-center justify-between text-[10px] ${muted}`}>
                          <span>
                            <i className="ri-time-line mr-0.5"></i>
                            {formatTimeAgo(item.capturedAt)}
                          </span>
                          <span className={`flex items-center gap-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            <i className="ri-text-wrap text-[10px]"></i>
                            {item.charCount} chars
                          </span>
                        </div>
                      </div>

                      <i className={`ri-arrow-right-line text-xs flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${
                        darkMode ? 'text-orange-400' : 'text-orange-500'
                      }`}></i>
                    </div>
                    {idx === 0 && (
                      <div className={`mt-2 pt-2 border-t text-[10px] font-semibold flex items-center gap-1 ${
                        darkMode ? 'border-gray-700 text-orange-400' : 'border-orange-100 text-orange-600'
                      }`}>
                        <i className="ri-history-line text-[10px]"></i>
                        Most recent
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className={`px-4 py-3 border-t text-center flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-[10px] ${muted}`}>
            <i className="ri-information-line mr-1"></i>
            {activeTab === 'drafts' ? 'Drafts stored locally in your browser' : `${promptHistory.length} of ${MAX_HISTORY} prompts logged`}
          </p>
        </div>
      </div>
    </>
  );
}
